import type { ArrowKey } from '@arrow/game-engine';

/** What a key press means to the game. The hook turns these into engine calls. */
export type GameAction =
	| { type: 'letter'; letter: string }
	| { type: 'backspace' }
	| { type: 'move'; dir: ArrowKey }
	| { type: 'nextAnswer'; delta: 1 | -1 }
	| { type: 'toggleDirection' };

export interface KeyLike {
	key: string;
	shiftKey?: boolean;
	metaKey?: boolean;
	ctrlKey?: boolean;
	altKey?: boolean;
	isComposing?: boolean;
}

const ARROWS: Record<string, ArrowKey> = {
	ArrowLeft: 'left',
	ArrowRight: 'right',
	ArrowUp: 'up',
	ArrowDown: 'down',
};

/**
 * Maps a physical key press to a game action, or null when the game shouldn't handle
 * it (so the browser keeps its own shortcuts: Cmd+R, Ctrl+C, …).
 */
export function mapKey(e: KeyLike): GameAction | null {
	if (e.metaKey || e.ctrlKey || e.altKey || e.isComposing) return null;
	const { key } = e;
	if (key.length === 1 && /^[a-z]$/i.test(key)) return { type: 'letter', letter: key.toUpperCase() };
	if (key === 'Backspace' || key === 'Delete') return { type: 'backspace' };
	const dir = ARROWS[key];
	if (dir) return { type: 'move', dir };
	if (key === 'Tab') return { type: 'nextAnswer', delta: e.shiftKey ? -1 : 1 };
	if (key === 'Enter') return { type: 'nextAnswer', delta: e.shiftKey ? -1 : 1 };
	if (key === ' ' || key === 'Spacebar') return { type: 'toggleDirection' };
	return null;
}

/** The on-screen keyboard's layout. `SWAP` toggles direction, `BACK` deletes. */
export const KEY_ROWS: readonly (readonly string[])[] = [
	[...'QWERTYUIOP'],
	[...'ASDFGHJKL'],
	['SWAP', ...'ZXCVBNM', 'BACK'],
];

/** Maps an on-screen key to its action. */
export function mapScreenKey(key: string): GameAction | null {
	if (key === 'BACK') return { type: 'backspace' };
	if (key === 'SWAP') return { type: 'toggleDirection' };
	return /^[A-Z]$/.test(key) ? { type: 'letter', letter: key } : null;
}
